import { AuthProvider, useAuth } from '@matix/auth';
import { useCashierAuthStore } from './store/cashierAuthStore';
import { isCallCenterAgent } from './lib/callCenterRouting';
import {
  CallCenterScreen,
  useCallCenterBootstrap,
} from './features/call-center';
import { LoginScreen as MtLoginScreen } from './features/auth/MtLoginScreen';
import { SubsidiaryPickerScreen } from './features/auth/SubsidiaryPickerScreen';
import { BranchPickerScreen } from './features/auth/BranchPickerScreen';
import { SetupScreen } from './features/auth/SetupScreen';
import { LoadingScreen } from './features/auth/PickerLayout';
import { LegacyApp } from './LegacyApp';

/**
 * Multi-tenant auth wrapper (Phase 8.1).
 *
 * Replaces the existing top-level <App /> routing. Flow:
 *
 *   - state.kind !== 'session' → render the multi-tenant pickers
 *   - state.kind === 'session' → bridge the session token into the legacy
 *     @matix/auth layer (so existing useAuth() still reports authenticated),
 *     then mount <LegacyApp /> which is the original cashier app body
 *     (sidebar, topbar, catalog, cart, modals, ...).
 *
 * The legacy app is unchanged. Once we hand it a valid session token, all
 * its existing API calls work as before — the JWT shape is identical to
 * what /api/v1/auth/login used to issue, just acquired via the new
 * 4-step flow instead.
 *
 * The session token is fed to the shared api-client when the auth store
 * reaches 'session' (and re-seeded on reload in main.tsx). We then mount
 * @matix/auth's <AuthProvider> ONLY around LegacyApp, so it reads that
 * valid token at mount → useAuth() reports authenticated and exposes the
 * decoded cashier (role, permissions, branch_id, …). Logging out from the
 * legacy UI resets the multi-tenant store, which returns us to the login.
 */
export function App() {
  const state = useCashierAuthStore((s) => s.state);

  switch (state.kind) {
    case 'unauthenticated':
      return <MtLoginScreen banner={state.banner} />;

    case 'logging-in':
      return <MtLoginScreen banner={null} />;

    case 'pending':
    case 'choosing-subsidiary':
      return (
        <SubsidiaryPickerScreen
          user={state.user}
          subsidiaries={state.subsidiaries}
          busy={state.kind === 'choosing-subsidiary'}
        />
      );

    case 'subsidiary-chosen':
    case 'choosing-branch':
      return (
        <BranchPickerScreen
          user={state.user}
          subsidiary={state.subsidiary}
          branches={state.branches}
          busy={state.kind === 'choosing-branch'}
        />
      );

    case 'branch-chosen':
    case 'choosing-drawer':
      // Drawer is auto-picked (first drawer in the branch) — no picker step.
      return <LoadingScreen message="Signing in…" />;

    case 'setup':
      return <SetupScreen />;

    case 'session':
      return (
        <AuthProvider onLogout={() => useCashierAuthStore.getState().logout()}>
          <SignedIn />
        </AuthProvider>
      );
  }
}

/**
 * Which surface a signed-in user gets.
 *
 * A call-centre agent is routed to the call-centre screen instead of the till.
 * Everyone else gets `LegacyApp` exactly as before — same shell, same tabs,
 * same behaviour, no code path changed. That is the contract for this feature:
 * a tenant that turns it on for nobody cannot tell it was added.
 *
 * One signal decides it — see `isCallCenterAgent`, which is where the reasoning
 * lives and what the test pins. It reads the JWT the pickers just obtained, so
 * the decision is made once at mount with no extra request.
 *
 * The bootstrap fetch below runs for EVERY user — it is what tells an ordinary
 * till whether to show a Pending Orders tab, and gating it on agent access
 * would mean no branch could ever receive a phone order.
 */
function SignedIn(): JSX.Element {
  const { isAuthenticated, cashier } = useAuth();
  const isAgent = isCallCenterAgent(cashier);

  useCallCenterBootstrap(isAuthenticated);

  return isAgent ? <CallCenterScreen /> : <LegacyApp />;
}
